/**
 * Centralized utility for generating SCIM PATCH operations
 * Ensures consistent operation generation across all components
 *
 * PATCH operation notes:
 * - Replace is applicable to all attribute types (single-value and multi-valued). Use replace with
 *   the new value (e.g. [] to clear an array, null to clear a single value).
 * - When clearing all array values, use replace with value: [] (or a delete/remove with no value
 *   to remove the attribute). Do not use remove with value: [] (remove with empty array).
 */

/**
 * Normalize a value for comparison - treats undefined, null, false, empty strings, and empty arrays as equivalent
 * Attribute ids (e.g. Attributes arrays) are kept as-is (no stripping of $. prefix).
 * Special handling for certain fields:
 * - ManualSetMember and Filter: empty arrays are kept as empty arrays (not normalized to undefined)
 * - Type: case-insensitive comparison
 * - IsSystem/isSystem: false values are preserved (not normalized to undefined)
 */
export const normalizeForComparison = (value: any, path?: string): any => {
    // Normalize attributes arrays (keep ids as-is, no stripping)
    if (path && (path === 'Attributes' || path.endsWith('.Attributes'))) {
        value = normalizeAttributes(value);
    }

    // Special handling for Type - normalize to lowercase for case-insensitive comparison
    const isType = path === 'Type' || path?.endsWith('.Type');
    if (isType && typeof value === 'string') {
        value = value.toLowerCase();
    }

    // Special handling for IsSystem/isSystem - preserve false values
    const isSystemField = path === 'IsSystem' || path === 'isSystem' || path?.endsWith('.IsSystem') || path?.endsWith('.isSystem');
    if (isSystemField && value === false) {
        return false; // Preserve false for isSystem fields
    }

    // Special handling for ManualSetMember and Filter - normalize undefined to empty array/string
    // This allows us to treat missing properties the same as empty arrays/strings
    const isManualSetMember = path === 'ManualSetMember' || path?.endsWith('.ManualSetMember');
    const isFilter = path === 'Filter' || path?.endsWith('.Filter');

    // For ManualSetMember and Filter, normalize undefined to empty array/string
    // This treats missing properties (undefined) the same as empty values
    if (isManualSetMember && value === undefined) {
        return []; // Normalize undefined to empty array for ManualSetMember
    }
    if (isFilter && value === undefined) {
        return ''; // Normalize undefined to empty string for Filter
    }

    // Treat undefined, null, false, empty strings, and empty arrays as equivalent (undefined)
    // BUT: Keep empty arrays for ManualSetMember and Filter so we can detect changes
    // AND: Keep false for IsSystem fields
    if (value === null || value === '') {
        return undefined;
    }
    // Preserve false for IsSystem fields, but normalize false to undefined for other fields
    if (value === false) {
        if (isSystemField) {
            return false; // Preserve false for IsSystem fields
        }
        return undefined; // Normalize false to undefined for other fields
    }
    // Treat empty arrays as undefined, EXCEPT for ManualSetMember and Filter
    if (Array.isArray(value) && value.length === 0) {
        if (isManualSetMember || isFilter) {
            return []; // Keep empty array for ManualSetMember and Filter
        }
        return undefined;
    }
    // If value is undefined, keep it as undefined
    if (value === undefined) {
        return undefined;
    }
    return value;
};

/**
 * Normalize attributes array (keep ids as-is; no stripping of $. prefix)
 */
const normalizeAttributes = (value: any): any => {
    if (Array.isArray(value)) {
        return value;
    }
    return value;
};

/**
 * Options for normalizeDataForComparison function
 */
export interface NormalizeDataOptions {
    /**
     * If true, remove editVersion and editDate from Config/Settings objects.
     * Default: false (preserve metadata)
     */
    excludeConfigMetadata?: boolean;
    /**
     * Custom normalization function for Body property (e.g., to normalize line endings)
     */
    normalizeBody?: (value: string) => string;
}

/**
 * Normalize data for comparison, with optional Config metadata exclusion
 * This is useful for diff display where you want to exclude internal metadata
 * 
 * @param data - Data to normalize
 * @param options - Normalization options
 * @returns Normalized data
 */
export const normalizeDataForComparison = (
    data: any,
    options: NormalizeDataOptions = {}
): any => {
    if (!data || typeof data !== 'object') {
        return data;
    }

    const { excludeConfigMetadata = false, normalizeBody } = options;

    const normalized = Array.isArray(data) ? [...data] : { ...data };

    // Remove editVersion and editDate from Config/Settings objects if requested
    const configLike = normalized.Config ?? normalized.Settings;
    if (excludeConfigMetadata && configLike && typeof configLike === 'object') {
        const configCopy = { ...configLike };
        delete configCopy.editVersion;
        delete configCopy.editDate;
        if (normalized.Config !== undefined) normalized.Config = configCopy;
        if (normalized.Settings !== undefined) normalized.Settings = configCopy;
    }

    // Normalize Body property if custom normalizer provided
    if (normalizeBody) {
        if (normalized.Body && typeof normalized.Body === 'string') {
            normalized.Body = normalizeBody(normalized.Body);
        }
        if (normalized.body && typeof normalized.body === 'string') {
            normalized.body = normalizeBody(normalized.body);
        }
    }

    // Recursively normalize nested objects
    for (const key in normalized) {
        if (normalized[key] && typeof normalized[key] === 'object' && !Array.isArray(normalized[key])) {
            normalized[key] = normalizeDataForComparison(normalized[key], options);
        } else if (Array.isArray(normalized[key])) {
            normalized[key] = normalized[key].map((item: any) => normalizeDataForComparison(item, options));
        }
    }

    return normalized;
};

/**
 * Normalize reference attributes (strings or objects with Id) to Id strings for comparison.
 * Exported for PATCH vs JSON-diff parity (compareObjects uses this for leaf values).
 */
export const normalizeReference = (value: any): any => {
    if (typeof value === 'string') {
        return value;
    }
    if (value && typeof value === 'object') {
        // If it's an object with Id, use the Id
        if ('Id' in value || 'id' in value) {
            return value.Id || value.id;
        }
        // If it's an array of references, normalize each one
        if (Array.isArray(value)) {
            return value.map(normalizeReference);
        }
    }
    return value;
};

/**
 * Options for compareObjects function
 */
export interface CompareObjectsOptions {
    /**
     * Paths to exclude from comparison (e.g., 'Id', 'EntityType')
     */
    excludedPaths?: string[];
    /**
     * If true, preserve false values instead of normalizing them to undefined.
     * Use true for diff display, false for PATCH operation generation.
     * Default: false (normalize false to undefined for PATCH operations)
     */
    preserveFalseValues?: boolean;
}

/**
 * Normalize a value for comparison with optional preservation of false values
 */
const normalizeForComparisonWithOptions = (value: any, path: string | undefined, preserveFalseValues: boolean): any => {
    // If preserving false values, only normalize null and empty strings
    if (preserveFalseValues) {
        if (value === null || value === '') {
            return undefined;
        }
        // Preserve false, true, numbers, strings, arrays, objects as-is
        return value;
    }

    // Otherwise use the standard normalization (normalizes false to undefined)
    return normalizeForComparison(value, path);
};

/**
 * Utility function to compare two objects and return differences
 * 
 * @param obj1 - Original object
 * @param obj2 - Current/modified object
 * @param path - Current path in the object hierarchy (for recursion)
 * @param options - Comparison options
 * @returns Object mapping paths to change types ('added' | 'removed' | 'changed')
 * 
 * @example
 * // For PATCH operations (normalize false to undefined)
 * const diffs = compareObjects(original, current, '', { excludedPaths: ['Id'] });
 * 
 * // For diff display (preserve false values)
 * const diffs = compareObjects(original, current, '', { preserveFalseValues: true });
 */
export const compareObjects = (
    obj1: any,
    obj2: any,
    path: string = '',
    options: CompareObjectsOptions | string[] = {}
): { [key: string]: 'added' | 'removed' | 'changed' } => {
    // Backward compatibility: if options is an array, treat it as excludedPaths
    const opts: CompareObjectsOptions = Array.isArray(options)
        ? { excludedPaths: options }
        : options;
    
    const excludedPaths = opts.excludedPaths || [];
    const preserveFalseValues = opts.preserveFalseValues ?? false;

    const differences: { [key: string]: 'added' | 'removed' | 'changed' } = {};

    // Get all keys from both objects
    const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);

    for (const key of allKeys) {
        const currentPath = path ? `${path}.${key}` : key;

        // Skip excluded paths
        if (excludedPaths.includes(currentPath)) {
            continue;
        }

        const val1 = obj1?.[key];
        const val2 = obj2?.[key];

        // Check if key exists in both objects (for determining added vs changed when preserving false)
        const keyExistsInObj1 = obj1 && key in obj1;
        const keyExistsInObj2 = obj2 && key in obj2;

        // Normalize reference attributes (strings or objects with Id) to Id strings for comparison
        // This handles cases where one side has expanded references and the other doesn't
        const refNormalizedVal1 = normalizeReference(val1);
        const refNormalizedVal2 = normalizeReference(val2);

        // Normalize values based on options
        const normalizedVal1 = normalizeForComparisonWithOptions(refNormalizedVal1, currentPath, preserveFalseValues);
        const normalizedVal2 = normalizeForComparisonWithOptions(refNormalizedVal2, currentPath, preserveFalseValues);

        // Determine change type
        if (normalizedVal1 === undefined && normalizedVal2 !== undefined) {
            differences[currentPath] = 'added';
        } else if (normalizedVal1 !== undefined && normalizedVal2 === undefined) {
            differences[currentPath] = 'removed';
        } else if (typeof normalizedVal1 === 'object' && typeof normalizedVal2 === 'object' && normalizedVal1 !== null && normalizedVal2 !== null && !Array.isArray(normalizedVal1) && !Array.isArray(normalizedVal2)) {
            // Recursively compare nested objects (but not arrays)
            const nestedDifferences = compareObjects(normalizedVal1, normalizedVal2, currentPath, opts);
            Object.assign(differences, nestedDifferences);
        } else if (JSON.stringify(normalizedVal1) !== JSON.stringify(normalizedVal2)) {
            // Values are different - determine if it's a change or add/remove
            if (preserveFalseValues) {
                // When preserving false values, check if key existed in both to distinguish changed vs added/removed
                if (keyExistsInObj1 && keyExistsInObj2) {
                    differences[currentPath] = 'changed';
                } else if (keyExistsInObj1) {
                    differences[currentPath] = 'removed';
                } else {
                    differences[currentPath] = 'added';
                }
            } else {
                // When normalizing false, if we got here both values are defined, so it's a change
                differences[currentPath] = 'changed';
            }
        }
    }

    return differences;
};

/**
 * Generate PATCH operations from differences
 * This is the centralized function that all components should use
 */
export const generatePatchOperations = (
    originalData: any,
    currentData: any,
    differences: { [key: string]: 'added' | 'removed' | 'changed' },
    excludedPaths: string[] = []
): any[] => {
    const operations: any[] = [];

    const getNestedValue = (obj: any, path: string): any => {
        return path.split('.').reduce((cur, key) => cur?.[key], obj);
    };

    const isEqual = (a: any, b: any): boolean => {
        // Normalize both values before comparison (note: path not available here, but arrays are handled recursively)
        const normalizedA = normalizeForComparison(a);
        const normalizedB = normalizeForComparison(b);

        // If both are arrays, normalize attributes in them
        if (Array.isArray(normalizedA) && Array.isArray(normalizedB)) {
            const normalizedAArray = normalizeAttributes(normalizedA);
            const normalizedBArray = normalizeAttributes(normalizedB);
            if (normalizedAArray.length !== normalizedBArray.length) return false;
            return normalizedAArray.every((val: any, i: number) => isEqual(val, normalizedBArray[i]));
        }

        // After normalization, both undefined means equal
        if (normalizedA === undefined && normalizedB === undefined) return true;
        if (normalizedA === normalizedB) return true;

        // If one is undefined and the other isn't, they're not equal
        if (normalizedA === undefined || normalizedB === undefined) return false;

        if (typeof normalizedA !== typeof normalizedB) return false;
        if (Array.isArray(normalizedA) && Array.isArray(normalizedB)) {
            if (normalizedA.length !== normalizedB.length) return false;
            return normalizedA.every((val, i) => isEqual(val, normalizedB[i]));
        }
        if (typeof normalizedA === 'object' && typeof normalizedB === 'object') {
            const keysA = Object.keys(normalizedA);
            const keysB = Object.keys(normalizedB);
            if (keysA.length !== keysB.length) return false;
            return keysA.every(k => isEqual(normalizedA[k], normalizedB[k]));
        }
        return false;
    };

    for (const [path, changeType] of Object.entries(differences)) {
        // Skip excluded paths
        if (excludedPaths.includes(path)) {
            continue;
        }

        const originalValue = normalizeForComparison(getNestedValue(originalData, path), path);
        const currentValue = normalizeForComparison(getNestedValue(currentData, path), path);
        const actualOriginalValue = getNestedValue(originalData, path);
        const actualCurrentValue = getNestedValue(currentData, path);

        // Special handling for IsSystem fields - always use the actual value, not normalized
        const isSystemField = path === 'IsSystem' || path === 'isSystem' || path?.endsWith('.IsSystem') || path?.endsWith('.isSystem');
        const actualValueForOperation = isSystemField ? actualCurrentValue : currentValue;

        if (changeType === 'added') {
            // Add operation is only for adding values to arrays
            // For single-value fields, use replace instead
            const isArrayField = Array.isArray(actualOriginalValue) || Array.isArray(actualCurrentValue);

            if (isArrayField) {
                operations.push({
                    op: 'add',
                    path: path,
                    value: actualValueForOperation
                });
            } else {
                // Single-value field: use replace
                operations.push({
                    op: 'replace',
                    path: path,
                    value: actualValueForOperation
                });
            }
        } else if (changeType === 'removed') {
            // Special handling for IsSystem fields - if being removed and it's false, use replace with null
            // For array fields when clearing all values: use replace with [] (not remove with empty array).
            // Replace is applicable to all types; a delete (remove with no value) would also work here.
            const isArrayField = Array.isArray(actualOriginalValue) || Array.isArray(actualCurrentValue);
            
            if (isSystemField) {
                // For IsSystem fields, use replace with null instead of remove
                operations.push({
                    op: 'replace',
                    path: path,
                    value: null
                });
            } else if (isArrayField) {
                // Clearing all array values: replace with [] (do not use remove with value: [])
                operations.push({
                    op: 'replace',
                    path: path,
                    value: []
                });
            } else {
                // For non-array fields, use replace with null
                operations.push({
                    op: 'replace',
                    path: path,
                    value: null
                });
            }
        } else if (changeType === 'changed') {
            // Special handling for IsSystem fields being set to false
            if (isSystemField && actualCurrentValue === false) {
                // When isSystem is set to false, use replace with null (or could use remove/delete)
                // Using replace with null to explicitly set the value
                operations.push({
                    op: 'replace',
                    path: path,
                    value: null
                });
            } else {
                // For other changes, use replace
                // For IsSystem fields, use the actual value (preserve false)
                // For other fields, if current value is undefined/null after normalization, use null
                const operationValue = isSystemField
                    ? actualCurrentValue
                    : (currentValue === undefined ? null : currentValue);

                operations.push({
                    op: 'replace',
                    path: path,
                    value: operationValue
                });
            }
        }
    }

    return operations;
};

/**
 * Generate PATCH operations with array field handling
 * For array fields, uses add/remove operations instead of replace when appropriate
 */
export const generatePatchOperationsWithArrays = (
    originalData: any,
    currentData: any,
    differences: { [key: string]: 'added' | 'removed' | 'changed' },
    arrayFields: string[] = [],
    excludedPaths: string[] = []
): any[] => {
    const operations: any[] = [];
    const arrayFieldsSet = new Set(arrayFields);

    const getNestedValue = (obj: any, path: string): any => {
        return path.split('.').reduce((cur, key) => cur?.[key], obj);
    };

    for (const [path, changeType] of Object.entries(differences)) {
        // Skip excluded paths
        if (excludedPaths.includes(path)) {
            continue;
        }

        const originalValue = getNestedValue(originalData, path);
        const currentValue = getNestedValue(currentData, path);
        const isArrayField = arrayFieldsSet.has(path);

        // Special handling for IsSystem fields - always use the actual value, not normalized
        const isSystemField = path === 'IsSystem' || path === 'isSystem' || path?.endsWith('.IsSystem') || path?.endsWith('.isSystem');
        const actualValueForOperation = isSystemField ? currentValue : normalizeForComparison(currentValue, path);

        if (changeType === 'added') {
            // Special handling for Filter field - empty string should not be added
            const isFilterField = path === 'Filter' || path?.endsWith('.Filter');
            if (isFilterField) {
                const normalizedCurrent = normalizeForComparison(currentValue, path);
                // If Filter is empty string or undefined after normalization, don't add it
                // (empty string and undefined are equivalent for Filter)
                if (normalizedCurrent === undefined || normalizedCurrent === '') {
                    // Skip operation - empty string and missing property are equivalent
                    continue;
                }
            }
            
            // Add operation is only for adding values to arrays
            // For single-value fields, use replace instead
            const isActuallyArray = Array.isArray(originalValue) || Array.isArray(currentValue);
            const shouldUseAdd = isArrayField && isActuallyArray;

            if (shouldUseAdd) {
                operations.push({
                    op: 'add',
                    path: path,
                    value: actualValueForOperation
                });
            } else {
                // Single-value field: use replace
                operations.push({
                    op: 'replace',
                    path: path,
                    value: actualValueForOperation
                });
            }
        } else if (changeType === 'removed') {
            // Special handling for IsSystem fields - if being removed and it's false, use replace with null
            // For array fields when clearing all values: use replace with [] (not remove with empty array).
            // Replace is applicable to all types; a delete (remove with no value) would also work here.
            const isActuallyArray = Array.isArray(originalValue) || Array.isArray(currentValue);
            
            if (isSystemField) {
                // For IsSystem fields, use replace with null instead of remove
                operations.push({
                    op: 'replace',
                    path: path,
                    value: null
                });
            } else if (isArrayField && isActuallyArray) {
                // Clearing all array values: replace with [] (do not use remove with value: [])
                operations.push({
                    op: 'replace',
                    path: path,
                    value: []
                });
            } else {
                // For non-array fields, use replace with null
                operations.push({
                    op: 'replace',
                    path: path,
                    value: null
                });
            }
        } else if (changeType === 'changed') {
            // For array fields, use add/remove operations when adding/removing items
            // Handle case where originalValue might be undefined/null (treat as empty array)
            if (isArrayField && Array.isArray(currentValue)) {
                // Normalize originalValue - if it's undefined/null, treat as empty array
                const normalizedOriginal = (originalValue && Array.isArray(originalValue)) ? originalValue : [];
                const normalizedCurrent = currentValue || [];
                
                // Check if we're adding items (current has more items than original)
                if (normalizedCurrent.length > normalizedOriginal.length) {
                    // Find items that were added
                    const addedItems = normalizedCurrent.filter(item =>
                        !normalizedOriginal.some(origItem => {
                            // Compare by Id if objects, or by value if primitives
                            if (typeof item === 'object' && item !== null) {
                                return (item.Id || item.id) === (origItem.Id || origItem.id);
                            }
                            return item === origItem;
                        })
                    );

                    // Single add operation with array of values (SCIM add accepts array)
                    const addedValues = addedItems.map((item: any) =>
                        typeof item === 'object' && item !== null ? (item.Id || item.id || item) : item
                    );
                    if (addedValues.length > 0) {
                        operations.push({
                            op: 'add',
                            path: path,
                            value: addedValues
                        });
                    }
                } else if (normalizedCurrent.length < normalizedOriginal.length) {
                    // Items were removed - use remove operation
                    const removedItems = normalizedOriginal.filter(origItem =>
                        !normalizedCurrent.some(item => {
                            if (typeof item === 'object' && item !== null) {
                                return (item.Id || item.id) === (origItem.Id || origItem.id);
                            }
                            return item === origItem;
                        })
                    );

                    for (const item of removedItems) {
                        // remove: for array elements only (value identifies which element to remove)
                        const operationValue = typeof item === 'object' && item !== null
                            ? (item.Id || item.id || item)
                            : item;
                        operations.push({
                            op: 'remove',
                            path: path,
                            value: operationValue
                        });
                    }
                } else {
                    // Same length but different items - use replace
                    operations.push({
                        op: 'replace',
                        path: path,
                        value: currentValue
                    });
                }
            } else {
                // Non-array fields or non-array changes - use replace
                // Special handling for Filter field - empty string should be treated as remove/not set
                const isFilterField = path === 'Filter' || path?.endsWith('.Filter');
                if (isFilterField) {
                    const normalizedCurrent = normalizeForComparison(currentValue, path);
                    // If Filter is empty string or undefined after normalization, don't include it in operations
                    // (empty string and undefined are equivalent for Filter)
                    if (normalizedCurrent === undefined || normalizedCurrent === '') {
                        // Skip operation - empty string and missing property are equivalent
                        continue;
                    }
                }
                
                // Special handling for IsSystem fields being set to false
                if (isSystemField && currentValue === false) {
                    // When isSystem is set to false, use replace with null (or could use delete/remove)
                    // Using replace with null to explicitly set the value
                    operations.push({
                        op: 'replace',
                        path: path,
                        value: null
                    });
                } else {
                    // For other changes, use replace
                    // For IsSystem fields, use the actual value (preserve false)
                    // For other fields, if current value is undefined/null after normalization, use null
                    const normalizedCurrent = normalizeForComparison(currentValue, path);
                    const operationValue = isSystemField
                        ? currentValue
                        : (normalizedCurrent === undefined ? null : normalizedCurrent);

                    operations.push({
                        op: 'replace',
                        path: path,
                        value: operationValue
                    });
                }
            }
        }
    }

    return operations;
};

